import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

/* Accounts payable (P1 §5.5, ADR-0024). Amounts: integer minor units, branch currency. */

const dateSchema = z.iso.date();
const amount = z.number().int().min(0).refine(Number.isSafeInteger);
const positiveAmount = z.number().int().positive().refine(Number.isSafeInteger);

export const INVOICE_STATUSES = [
  'draft',
  'under_review',
  'approved',
  'posted',
  'partially_paid',
  'paid',
  'disputed',
  'voided',
] as const;
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const MATCH_REASONS = [
  'line_not_on_po',
  'qty_exceeds_ordered',
  'qty_exceeds_received',
  'price_variance',
  'amount_variance',
] as const;

export const apSettingsSchema = z.object({
  price_tolerance_bps: z.number().int(),
  qty_tolerance_bps: z.number().int(),
  amount_tolerance_minor: z.number().int(),
  default_match_mode: z.enum(['two_way', 'three_way']),
  version: z.number().int(),
});
export const updateApSettingsRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  price_tolerance_bps: z.number().int().min(0).max(10_000).optional(),
  qty_tolerance_bps: z.number().int().min(0).max(10_000).optional(),
  amount_tolerance_minor: amount.optional(),
  default_match_mode: z.enum(['two_way', 'three_way']).optional(),
});

export const invoiceLineSchema = z.object({
  id: uuidSchema,
  line_no: z.number().int(),
  purchase_order_line_id: uuidSchema.nullable(),
  item_id: uuidSchema.nullable(),
  description: z.string(),
  qty_scaled: z.number().int(),
  unit: z.string().nullable(),
  unit_price_minor: z.number().int(),
  discount_minor: z.number().int(),
  tax_rate_bps: z.number().int(),
  net_minor: z.number().int(),
  tax_minor: z.number().int(),
});

export const matchLineResultSchema = z.object({
  invoice_line_id: uuidSchema.nullable(),
  purchase_order_line_id: uuidSchema.nullable(),
  ordered_qty_scaled: z.number().int().nullable(),
  received_qty_scaled: z.number().int().nullable(),
  invoiced_qty_scaled: z.number().int().nullable(),
  po_price_minor: z.number().int().nullable(),
  invoice_price_minor: z.number().int().nullable(),
  qty_variance_scaled: z.number().int().nullable(),
  price_variance_minor: z.number().int().nullable(),
  value_variance_minor: z.number().int().nullable(),
  reasons: z.array(z.string()),
});

export const invoiceSchema = z.object({
  id: uuidSchema,
  document_number: z.string(),
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  invoice_number: z.string(),
  duplicate_override: z.boolean(),
  duplicate_reason: z.string().nullable(),
  purchase_order_id: uuidSchema.nullable(),
  po_number: z.string().nullable(),
  status: invoiceStatusSchema,
  match_mode: z.enum(['two_way', 'three_way', 'none']),
  match_status: z.enum(['not_matched', 'matched', 'exception', 'overridden']),
  currency: z.string(),
  invoice_date: dateSchema,
  due_date: dateSchema,
  net_minor: z.number().int(),
  tax_minor: z.number().int(),
  total_minor: z.number().int(),
  /** Open payable: posted total − applied payments and credits. 0 before posting. */
  outstanding_minor: z.number().int(),
  /** Allocations recorded but not yet verified / posted. */
  pending_allocations_minor: z.number().int(),
  budget_id: uuidSchema.nullable(),
  notes: z.string().nullable(),
  dispute_reason: z.string().nullable(),
  approval_request_id: uuidSchema.nullable(),
  created_by_name: z.string().nullable(),
  posted_by_name: z.string().nullable(),
  posted_at: isoTimestampSchema.nullable(),
  voided_at: isoTimestampSchema.nullable(),
  void_reason: z.string().nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  lines: z.array(invoiceLineSchema),
  match: z.object({
    run_at: isoTimestampSchema.nullable(),
    lines: z.array(matchLineResultSchema),
  }),
});
export type SupplierInvoice = z.infer<typeof invoiceSchema>;
export const invoiceListResponseSchema = z.object({
  items: z.array(invoiceSchema.omit({ lines: true, match: true })),
});
export const invoiceListQuerySchema = z.object({
  status: invoiceStatusSchema.optional(),
  supplier_id: uuidSchema.optional(),
  open: z.enum(['true', 'false']).optional(),
});

const invoiceLineInput = z.object({
  purchase_order_line_id: uuidSchema.nullable().default(null),
  description: z.string().trim().min(1).max(200).optional(),
  qty_scaled: z.number().int().positive().refine(Number.isSafeInteger),
  unit_price_minor: amount,
  discount_minor: amount.default(0),
  tax_rate_bps: z.number().int().min(0).max(10_000).default(0),
});
export const createInvoiceRequestSchema = z.object({
  supplier_id: uuidSchema,
  invoice_number: z.string().trim().min(1).max(60),
  purchase_order_id: uuidSchema.nullable().default(null),
  /** Default: tenant setting (`three_way`) with a PO, `none` without. */
  match_mode: z.enum(['two_way', 'three_way']).optional(),
  invoice_date: dateSchema,
  /** Default: invoice date + supplier payment terms. */
  due_date: dateSchema.optional(),
  /** Header amounts as printed on the supplier's invoice (checked against the lines). */
  net_minor: amount,
  tax_minor: amount,
  budget_id: uuidSchema.nullable().optional(),
  notes: z.string().trim().min(1).max(500).nullable().default(null),
  /** Controlled exception to the duplicate number policy; needs `ap.approve`. */
  allow_duplicate_reason: z.string().trim().min(3).max(300).optional(),
  lines: z.array(invoiceLineInput).min(1).max(200),
});
export const updateInvoiceRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  invoice_number: z.string().trim().min(1).max(60).optional(),
  invoice_date: dateSchema.optional(),
  due_date: dateSchema.optional(),
  net_minor: amount.optional(),
  tax_minor: amount.optional(),
  budget_id: uuidSchema.nullable().optional(),
  notes: z.string().trim().min(1).max(500).nullable().optional(),
  lines: z.array(invoiceLineInput).min(1).max(200).optional(),
});
export const invoiceActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const invoiceReasonRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});

// ---- Credit notes

export const creditNoteSchema = z.object({
  id: uuidSchema,
  supplier_id: uuidSchema,
  invoice_id: uuidSchema,
  invoice_document_number: z.string(),
  credit_number: z.string(),
  amount_minor: z.number().int(),
  reason: z.string(),
  warehouse_exception_id: uuidSchema.nullable(),
  status: z.enum(['draft', 'posted', 'voided']),
  created_by: uuidSchema,
  created_by_name: z.string().nullable(),
  posted_by_name: z.string().nullable(),
  posted_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
});
export const creditNoteListResponseSchema = z.object({ items: z.array(creditNoteSchema) });
export const createCreditNoteRequestSchema = z.object({
  invoice_id: uuidSchema,
  credit_number: z.string().trim().min(1).max(60),
  amount_minor: positiveAmount,
  reason: z.string().trim().min(3).max(300),
});

// ---- Payments

export const apPaymentSchema = z.object({
  id: uuidSchema,
  document_number: z.string(),
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  channel: z.enum(['bank_transfer', 'cash', 'giro', 'other']),
  reference: z.string().nullable(),
  amount_minor: z.number().int(),
  currency: z.string(),
  payment_date: dateSchema,
  status: z.enum(['pending_verification', 'verified', 'rejected']),
  recorded_by: uuidSchema,
  recorded_by_name: z.string().nullable(),
  verified_by_name: z.string().nullable(),
  verified_at: isoTimestampSchema.nullable(),
  rejection_reason: z.string().nullable(),
  version: z.number().int(),
  allocations: z.array(
    z.object({
      invoice_id: uuidSchema,
      document_number: z.string(),
      invoice_number: z.string(),
      amount_minor: z.number().int(),
      status: z.enum(['pending', 'applied', 'reversed']),
    }),
  ),
});
export type ApPayment = z.infer<typeof apPaymentSchema>;
export const apPaymentListResponseSchema = z.object({ items: z.array(apPaymentSchema) });
export const apPaymentListQuerySchema = z.object({
  status: z.enum(['pending_verification', 'verified', 'rejected']).optional(),
});
export const createApPaymentRequestSchema = z.object({
  supplier_id: uuidSchema,
  channel: z.enum(['bank_transfer', 'cash', 'giro', 'other']),
  reference: z.string().trim().min(1).max(80).nullable().default(null),
  payment_date: dateSchema,
  /** Every rupiah is allocated: the allocations must add up to the amount. */
  amount_minor: positiveAmount,
  allocations: z
    .array(z.object({ invoice_id: uuidSchema, amount_minor: positiveAmount }))
    .min(1)
    .max(100),
});
export const apPaymentActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const apPaymentRejectRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});

// ---- Aging (P1 §6.5)

export const AGING_BUCKETS = ['not_due', 'd0_30', 'd31_60', 'd61_90', 'd90_plus'] as const;
const buckets = z.object({
  not_due: z.number().int(),
  d0_30: z.number().int(),
  d31_60: z.number().int(),
  d61_90: z.number().int(),
  d90_plus: z.number().int(),
  total: z.number().int(),
});
export const agingQuerySchema = z.object({ as_of: dateSchema.optional() });
export const agingResponseSchema = z.object({
  currency: z.string(),
  as_of: dateSchema,
  definitions: z.record(z.string(), z.string()),
  suppliers: z.array(
    z.object({
      supplier_id: uuidSchema,
      supplier_name: z.string(),
      buckets,
      invoices: z.array(
        z.object({
          invoice_id: uuidSchema,
          document_number: z.string(),
          invoice_number: z.string(),
          due_date: dateSchema,
          days_overdue: z.number().int(),
          bucket: z.enum(AGING_BUCKETS),
          open_minor: z.number().int(),
        }),
      ),
    }),
  ),
  totals: buckets,
});
export type ApAging = z.infer<typeof agingResponseSchema>;
